/**
 * Underwriting Question Service
 * Calls the .NET UserVehicleSurvey API to save underwriting answers.
 */

import { apiFetch } from "../utils/api";

const BASE = "/UserVehicleSurvey";

async function post<T = void>(endpoint: string, body: object): Promise<T> {
  const response = await apiFetch(`${BASE}${endpoint}`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let errorMessage = `Backend error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // use default
    }
    throw new Error(errorMessage);
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Save vehicle usage (شخصي / أوبر-كريم / شركات)
 * VehicleUsageId: 1=شخصي, 2=أوبر-كريم, 3=شركات
 */
export async function saveVehicleUsage(vehicleUsageId: number): Promise<void> {
  await post("/vehicle-usage", { vehicleUsageId });
}

/**
 * Save had accident last year
 */
export async function saveHadAccidentLastYear(hadAccidentLastYear: boolean): Promise<void> {
  await post("/had-accident-last-year", { hadAccidentLastYear });
}

/**
 * Save night parking location
 * NightParkingLocationId: 1=جراج خاص, 2=جراج العمارة, 3=في الشارع, 4=داخل كمبوند
 */
export async function saveNightParkingLocation(nightParkingLocationId: number): Promise<void> {
  await post("/night-parking-location", { nightParkingLocationId });
}

/**
 * Save total violations last year
 */
export async function saveTotalViolationsLastYear(totalViolationsLastYear: number): Promise<void> {
  await post("/total-violations-last-year", { totalViolationsLastYear });
}

/**
 * Save has tracking device
 */
export async function saveHasTrackingDevice(hasTrackingDevice: boolean): Promise<void> {
  await post("/has-tracking-device", { hasTrackingDevice });
}

/**
 * Save vehicle financing (Yes/No mapping: Yes=1, No=4 for now)
 * VehicleFinancingId: 1=تمويل بنكي, 2=شركة تقسيط, 3=حظر بيع, 4=لا يوجد
 */
export async function saveVehicleFinancing(vehicleFinancingId: number): Promise<void> {
  await post("/vehicle-financing", { vehicleFinancingId });
}

/**
 * Save number of drivers
 */
export async function saveNumberOfDrivers(numberOfDrivers: number): Promise<void> {
  await post("/number-of-drivers", { numberOfDrivers });
}
